import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import message from 'antd/lib/message'
import moment from 'moment-timezone'
import { useHistory } from 'react-router-dom'
import { useAuthState, useUpdateUser } from 'hooks'
import { analytics, openNotificationWithMsg } from 'helpers'
import { PREFERENCE_OPTIONS } from 'constants/user'

import { AgeRangeSlider, Button, CloseIcon } from 'components'
import Modal from 'components/AuthModal/components/Modal'
import Overlay from 'components/AuthModal/components/Overlay'
import { OVERLAY } from 'components/AuthModal/styles'
import ImageIcon from './ImageIcon'
import Location from './Location'
import Select from './Select'
import OrientationWarning from './OrientationWarning'

import userImage from 'assets/images/user.png'
import pinImage from 'assets/images/pin.png'
import starImage from 'assets/images/star.png'
import { ROUTES } from 'helpers/constants'
import LocationRangeSlider from 'components/LocationRangeSlider'

const EventPreferencesModal = ({ show, onClose }) => {
  const [editedUser, setEditedUser] = useState({})
  const [updatedValues, setUpdatedValues] = useState({})
  const [isSaving, setIsSaving] = useState(false)
  const [showOrientationWarning, setShowOrientationWarning] = useState(false)

  const { user } = useAuthState()
  const { update } = useUpdateUser()
  const history = useHistory()

  useEffect(() => {
    if (user) setEditedUser(user)
  }, [user, show])

  const handleChange = updates => {
    let modifiedUpdates = {
      ...updates,
      profile: updates?.profile,
    }

    setEditedUser({
      ...editedUser,
      ...modifiedUpdates,
    })
    setUpdatedValues({
      ...updatedValues,
      ...modifiedUpdates,
    })
  }

  const handleSave = useCallback(async () => {
    if (!Object.keys(updatedValues).length) {
      return onClose()
    }

    if (
      typeof updatedValues.location !== 'undefined' &&
      (!updatedValues.geoLat || !updatedValues.geoLat)
    ) {
      return openNotificationWithMsg('error', 'Please fill out your location!')
    }

    message.loading('saving...', 0)
    setIsSaving(true)

    try {
      const response = await update(updatedValues)
      if (response) {
        message.destroy()
        setUpdatedValues({})
        message.success('Preferences Saved', 2.5)
        analytics.segmentTrack('User Updated Settings', updatedValues)
        onClose(true)
      }
    } catch (error) {
      message.destroy()
      message.error(error.message || 'Please try again')
    } finally {
      setIsSaving(false)
    }
  }, [update, updatedValues, onClose])

  const handleInputChange = ({ target: { name, value } }) => handleChange({ [name]: value })

  const handleRouteChange = useCallback(
    route => {
      onClose()
      setShowOrientationWarning(false)
      history.push(route)
    },
    [history, onClose]
  )

  return (
    <>
      <Overlay show={show} className={OVERLAY} preventEscBackdropClose={true} onClose={onClose}>
        <Modal innerClassName="bg-darkBlack rounded-t-3xl overflow-y-scroll md:rounded-xl">
          <div className="relative text-white w-full p-5 md:p-7 md:w-108">
            <div className="flex justify-between items-center mb-7">
              <button
                className="bg-white bg-opacity-20 w-6 h-6 rounded-full flex items-center justify-center"
                onClick={() => onClose()}
              >
                <CloseIcon className="w-2 h-2 text-white" />
              </button>
              <div className="font-ownersWide text-xl">Preferences</div>
              <Button className="text-base" loading={isSaving} onClick={handleSave}>
                Save
              </Button>
            </div>
            <div className="md:px-5">
              <div>
                <AgeRangeSlider
                  icon={<ImageIcon iconImg={userImage} iconImgAlt={'Age range'} />}
                  className="bg-midnightBlack"
                  min={editedUser.profile?.desiredMinAge}
                  max={editedUser.profile?.desiredMaxAge}
                  onChange={({ min, max }) => {
                    handleChange({ profile: { desiredMinAge: min, desiredMaxAge: max } })
                  }}
                />
                <Location
                  iconImg={pinImage}
                  iconImgAlt="Location"
                  initialLocation={editedUser.location}
                  onLocationUpdate={({ geoLat, geoLng, location }) => {
                    setUpdatedValues({
                      ...updatedValues,
                      geoLat,
                      geoLng,
                      location,
                      timestamp: moment().unix(),
                    })
                    setEditedUser({
                      ...editedUser,
                      geoLat,
                      geoLng,
                      location,
                      timestamp: moment().unix(),
                    })
                  }}
                />
                <LocationRangeSlider
                  icon={<ImageIcon iconImg={pinImage} iconImgAlt={'Age range'} />}
                  className="bg-midnightBlack mt-2.5"
                  value={editedUser.offsetRadius}
                  onChange={value => handleChange({ offsetRadius: value })}
                />
                <Select
                  placeholder="-"
                  name="preference"
                  options={PREFERENCE_OPTIONS}
                  onChange={handleInputChange}
                  value={editedUser.preference}
                  iconImg={starImage}
                  iconImgAlt="Sexuality"
                  disabled={true}
                  onClick={() => setShowOrientationWarning(true)}
                />
              </div>
              <div className="w-full border-0 border-t border-solid border-white border-opacity-20 my-7 py-0.5" />
              <div className="py-7 px-5 bg-midnightBlack rounded-xl md:px-10">
                <div className="text-xl mb-3">For you</div>
                <div className="text-sm opacity-50 mb-7">
                  Go on dates with people who share your interests, passions, and beliefs.
                </div>
                <Button
                  className="w-full bg-azureBlue text-base py-3.5"
                  onClick={() => handleRouteChange(ROUTES.PROFILE)}
                >
                  Complete Profile
                </Button>
              </div>
            </div>
          </div>
        </Modal>
      </Overlay>
      <OrientationWarning
        show={showOrientationWarning}
        onClose={() => setShowOrientationWarning(false)}
        onRouteChange={handleRouteChange}
      />
    </>
  )
}

EventPreferencesModal.propTypes = {
  show: PropTypes.bool,
  onClose: PropTypes.func,
}

export default EventPreferencesModal
